import { motion } from 'framer-motion'
import { Calendar, Users, DollarSign } from 'lucide-react'
import type { Camp } from '@/types/api'
import { formatCurrency, cn } from '@/lib/utils'

const seasonStyles: Record<string, string> = {
  Summer: 'bg-cosmos-gold/15 text-cosmos-gold-dark dark:text-cosmos-gold',
  Competitive: 'bg-cosmos-green/15 text-cosmos-green-dark dark:text-cosmos-green-light',
  Holiday: 'bg-destructive/15 text-destructive',
  Advance: 'bg-pitch-900/15 text-pitch-900 dark:bg-cosmos-green/15 dark:text-cosmos-green-light',
}

export function CampsGrid({ camps }: { camps: Camp[] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {camps.map((c, i) => (
        <motion.article
          key={c.slug}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10% 0px' }}
          transition={{ delay: i * 0.08, duration: 0.5 }}
          className="rounded-2xl border border-border bg-card overflow-hidden hover:shadow-lg hover:border-cosmos-green/50 hover:-translate-y-1 transition-all"
        >
          <div className="aspect-video relative overflow-hidden bg-pitch-800">
            <img src={c.image} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <span className={cn('absolute top-3 left-3 rounded px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest', seasonStyles[c.season])}>
              {c.season}
            </span>
          </div>
          <div className="p-5">
            <h3 className="font-display text-xl uppercase leading-tight">{c.name}</h3>
            <p className="text-xs text-cosmos-green-dark dark:text-cosmos-gold mt-1 italic">{c.tagline}</p>
            <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{c.description}</p>
            <div className="mt-4 pt-3 border-t border-dashed border-border grid grid-cols-3 gap-2 text-xs text-muted-foreground">
              <MetaChip icon={<Calendar className="h-3 w-3" />} label={c.duration} />
              <MetaChip icon={<Users className="h-3 w-3" />} label={c.ageRange} />
              <MetaChip icon={<DollarSign className="h-3 w-3" />} label={formatCurrency(c.price)} strong />
            </div>
          </div>
        </motion.article>
      ))}
    </div>
  )
}

function MetaChip({ icon, label, strong }: { icon: React.ReactNode; label: string; strong?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-cosmos-green">{icon}</span>
      <span className={cn('font-display uppercase text-xs tracking-wide', strong ? 'text-cosmos-green' : 'text-foreground')}>{label}</span>
    </div>
  )
}
